import { IndustryType, SectionType } from '../types';

export interface IndustryFeature {
  id: string;
  name: string;
  description: string;
  isCore: boolean;
}

export interface IndustryModuleConfig {
  id: IndustryType;
  name: string;
  slug: string;
  badge: string;
  description: string;
  icon: string; // Lucide icon identifier
  color: string;
  accentColor: string;
  templateIds: string[];
  enabledModules: {
    pages: boolean;
    services: boolean;
    about: boolean;
    team: boolean;
    references: boolean;
    gallery: boolean;
    faq: boolean;
    contact: boolean;
    communications: boolean;
    blog: boolean;
    yachts: boolean;
    yachtDetails: boolean;
    routes: boolean;
    marinas: boolean;
    reservation: boolean;
    calendar: boolean;
    seo: boolean;
    domains: boolean;
  };
  allowedSections: SectionType[];
  features: IndustryFeature[];
}

export const INDUSTRIES: Record<IndustryType, IndustryModuleConfig> = {
  CORPORATE: {
    id: 'CORPORATE',
    name: 'Kurumsal & Şirket',
    slug: 'kurumsal',
    badge: 'Holding, Finans, Teknoloji, Danışmanlık',
    description:
      'Holdingler, danışmanlık şirketleri, teknoloji firmaları, hukuk büroları, mimarlık ofisleri ve kurumsal işletmeler için prestijli dijital kimlik ve içerik yönetim altyapısı.',
    icon: 'Building2',
    color: '#0F172A',
    accentColor: '#2563EB',
    templateIds: [
      'corp-apex',
      'corp-vanguard',
      'corp-minimal',
      'corp-novatech',
      'corp-pulse',
    ],
    enabledModules: {
      pages: true,
      services: true,
      about: true,
      team: true,
      references: true,
      gallery: true,
      faq: true,
      contact: true,
      communications: true,
      blog: true,
      yachts: false,
      yachtDetails: false,
      routes: false,
      marinas: false,
      reservation: false,
      calendar: false,
      seo: true,
      domains: true,
    },
    allowedSections: [
      'HEADER',
      'HERO',
      'STATS',
      'ABOUT',
      'SERVICES',
      'REFERENCES',
      'TESTIMONIALS',
      'TEAM',
      'GALLERY',
      'FAQ',
      'PRICING',
      'BLOG_PREVIEW',
      'CONTACT',
      'CUSTOM_CONTENT',
      'FOOTER',
    ],
    features: [
      { id: 'feat_services', name: 'Hizmet Kartları & Detaylar', description: 'Uzmanlık alanları ve çözümler için dinamik kartlar', isCore: true },
      { id: 'feat_team', name: 'Yönetim & Uzman Kadro', description: 'Ekip üyeleri, unvanlar ve sosyal profil linkleri', isCore: true },
      { id: 'feat_references', name: 'Referanslar & Marka Logoları', description: 'İş ortakları ve müşteri logoları ızgarası', isCore: true },
      { id: 'feat_blog', name: 'Kurumsal Blog & Haberler', description: 'SEO uyumlu makale ve bülten yayınlama sistemi', isCore: true },
      { id: 'feat_contact', name: 'İletişim & Harita Entegrasyonu', description: 'İletişim formu, WhatsApp ve harita navigasyonu', isCore: true },
      { id: 'feat_seo', name: 'İleri Seviye SEO & Meta Yönetimi', description: 'Site ve sayfa bazlı arama motoru optimizasyonu', isCore: true },
    ],
  },
  YACHT_RENTAL: {
    id: 'YACHT_RENTAL',
    name: 'Yat Kiralama & Gulet Charter',
    slug: 'yat-kiralama',
    badge: 'Lüks Yat, Gulet, Katamaran, Mavi Tur',
    description:
      'Lüks motor yat, gulet, katamaran ve yelkenli filosu yönetimi, detaylı tekne sayfaları, rota rehberleri ve takvimli online rezervasyon talep sistemi.',
    icon: 'Ship',
    color: '#0369A1',
    accentColor: '#0EA5E9',
    templateIds: [
      'yacht-azure',
      'yacht-poseidon',
      'yacht-nautilus',
    ],
    enabledModules: {
      pages: true,
      services: false,
      about: true,
      team: false,
      references: false,
      gallery: true,
      faq: true,
      contact: true,
      communications: true,
      blog: true,
      yachts: true,
      yachtDetails: true,
      routes: true,
      marinas: true,
      reservation: true,
      calendar: true,
      seo: true,
      domains: true,
    },
    allowedSections: [
      'HEADER',
      'HERO',
      'YACHT_FLEET',
      'YACHT_FILTER',
      'RESERVATION_CTA',
      'YACHT_ROUTES',
      'YACHT_MARINAS',
      'AVAILABILITY_CALENDAR',
      'STATS',
      'ABOUT',
      'GALLERY',
      'TESTIMONIALS',
      'FAQ',
      'BLOG_PREVIEW',
      'CONTACT',
      'CUSTOM_CONTENT',
      'FOOTER',
    ],
    features: [
      { id: 'feat_fleet', name: 'Yat & Tekne Filosu Vitrini', description: 'Kapasite, kabin, boy ve fiyat filtreli yat vitrini', isCore: true },
      { id: 'feat_yacht_details', name: 'Detaylı Tekne Sayfaları', description: 'Teknik özellikler, donanımlar ve görsel galeriler', isCore: true },
      { id: 'feat_reservation', name: 'Rezervasyon Talep Yönetimi', description: 'Müşteri kiralama talepleri ve durum takibi', isCore: true },
      { id: 'feat_calendar', name: 'Müsaitlik & Rezervasyon Takvimi', description: 'Tarih bazlı doluluk ve operasyon planlama', isCore: true },
      { id: 'feat_destinations', name: 'Mavi Tur & Rota Tanıtımı', description: 'Koylar, adalar ve özel charter rotaları', isCore: true },
      { id: 'feat_seo', name: 'Yat SEO & Sosyal Paylaşım', description: 'Her yat için optimize edilmiş arama motoru etiketleri', isCore: true },
    ],
  },
};

export const getIndustryConfig = (industry: IndustryType): IndustryModuleConfig => {
  return INDUSTRIES[industry] || INDUSTRIES.CORPORATE;
};

export const isFeatureEnabledForIndustry = (
  industry: IndustryType,
  feature: keyof IndustryModuleConfig['enabledModules']
): boolean => {
  const config = getIndustryConfig(industry);
  return Boolean(config.enabledModules[feature]);
};
